import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  fetchAuthenticatedUser,
  getCurrentUser,
  isAuthenticated,
  loginUser,
  logoutUser,
} from "../services/authService";
import {
  getAuthStateEventName,
  hasStoredSession,
} from "../services/authStorage";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getCurrentUser());
  const [initializing, setInitializing] = useState(() => hasStoredSession());

  const login = useCallback(async (credentials = {}) => {
    const nextUser = await loginUser(credentials);
    setUser(nextUser);
    return nextUser;
  }, []);

  const logout = useCallback(async () => {
    await logoutUser();
    setUser(null);
  }, []);

  useEffect(() => {
    let active = true;

    if (!hasStoredSession()) {
      setInitializing(false);
      return undefined;
    }

    fetchAuthenticatedUser()
      .then((nextUser) => {
        if (active) {
          setUser(nextUser);
        }
      })
      .catch(() => {
        if (active) {
          setUser(null);
        }
      })
      .finally(() => {
        if (active) {
          setInitializing(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const syncAuthState = () => {
      setUser(getCurrentUser());
      setInitializing(false);
    };

    window.addEventListener("storage", syncAuthState);
    window.addEventListener(getAuthStateEventName(), syncAuthState);

    return () => {
      window.removeEventListener("storage", syncAuthState);
      window.removeEventListener(getAuthStateEventName(), syncAuthState);
    };
  }, []);

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      initializing,
      isAuthenticated: Boolean(user) || isAuthenticated(),
    }),
    [initializing, login, logout, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider.");
  }

  return context;
}
